import { EditOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { getUrl } from "aws-amplify/storage";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import HorizontalSlider from "../../components/HorizontalSlider";
import InfoRow from "../../components/InfoRow";
import ProgressionChart from "../../components/report/charts/ProgressionChart";
import { useApi } from "../../contexts/ApiContext";
import { useApp } from "../../contexts/AppContext";
import { useAuth } from "../../contexts/AuthContext";
import { useError } from "../../contexts/ErrorContext";
import { fontSizes } from "../../theme";
import { getOocyteImagePath } from "../CustomizeReport/types";
import { GetCycleResponse, GetCycleResponseItem, scoretoColor } from "./types";
import { scoretoText } from "../../types";
import SubmitButton from "../../components/SubmitButton";
import { Pencil } from "lucide-react";
import Progress from "../../components/Progress";

interface progressionDataItem {
  stage: string;
  percentage: number;
}

interface OocyteData {
  image: string;
  index: number;
  score: number;
  fertilization: number;
  cleavage: number;
  blastulation: number;
  progressionData: progressionDataItem[];
  notes?: string;
  retrievalDate: Date | null;
  timePostHarvesting: number | null;
  invalid: boolean;
  condition: string;
}

const NoBorderTextField = styled(TextField)(({ theme }) => {
  return {
    "& .MuiOutlinedInput-root": {
      width: 60,
      "& fieldset": {
        border: "none",
      },
      "&:hover fieldset": {
        border: "none",
      },
      "&.Mui-focused fieldset": {
        border: "none",
      },
    },
    "& .MuiInputBase-formControl": {
      backgroundColor: "transparent!important",
    },
    "& .MuiInputBase-input": {
      boxShadow: "none",
      outline: "none",
      fontSize: "1.4285714285714284rem",
      fontWeight: theme.typography["report.headline"].fontWeight,
    },
  };
});

const StyledOocyteIndex = styled(Button)(() => ({
  backgroundColor: "#F5F5F4!important",
  color: "white",
  border: "none",
  borderRadius: 2,
  padding: "4px 8px",
  fontSize: 12,
  fontWeight: "bold",
  textTransform: "none",
  "&.selected": {
    backgroundColor: "#ffffff!important",
  },
}));

const StyledCircle = styled(Box)(() => ({
  width: 12,
  height: 12,
  borderRadius: "50%",
  paddingLeft: 4,
}));

const Details: React.FC = () => {
  const { cycleId, oocyteId } = useParams();
  const navigate = useNavigate();
  const { showError, showSuccess } = useError();
  const { updateBreadcrumbs, translate } = useApp();
  const { apiService, setSubmitting, setLoading, isLoading } = useApi();
  const { user } = useAuth();

  const [oocytes, setOocytes] = useState<OocyteData[]>([]);
  const [selectedOocyte, setSelectedOocyte] = useState<number>(1);
  const [notesEditMode, setNotesEditMode] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>("");
  const [score, setScore] = useState<number>(0);
  const [scoreEditMode, setScoreEditMode] = useState<boolean>(false);
  const [imageUrls, setImageUrls] = useState<{ [id: string]: string }>({});

  useEffect(() => {
    if (!selectedOocyte) {
      return;
    }
    navigate(`/oocyte/${cycleId}/${selectedOocyte}`, { replace: true });
  }, [selectedOocyte]);

  const translations = useMemo(() => translate("oocyte"), [translate]);

  const loadImages = (cycle: GetCycleResponseItem) => {
    for (const image of cycle.images) {
      getUrl({
        path: getOocyteImagePath(user.clinic, cycle.id, image.id, image.filename),
      }).then((response) => {
        setImageUrls((prev) => ({
          ...prev,
          [image.id]: response.url.href,
        }));
      });
    }
  };

  useEffect(() => {
    const index = oocytes.findIndex((o) => o.index === selectedOocyte);
    if (index === -1) {
      setSelectedOocyte(oocytes[0]?.index);
      return;
    }
    setNotes(oocytes[index].notes || "");
    setScore(oocytes[index].score);
  }, [selectedOocyte, oocytes]);

  useEffect(() => {
    setLoading(true);
    apiService
      .get<GetCycleResponse>(`/oocyte/${cycleId}`)
      .then((response) => {
        setOocytes(
          response.cycle.images.sort((a, b) => a.id > b.id ? 1 : -1).map((image) => ({
            image: "",
            index: image.id,
            score: image.u_overall ?? 0,
            fertilization: image.p_fertilization,
            cleavage: image.p_cleavage,
            blastulation: image.p_blastulation,
            progressionData: [],
            notes: image.u_notes,
            retrievalDate: response.cycle.retrieval_date ? new Date(response.cycle.retrieval_date) : null,
            timePostHarvesting: image?.hph,
            invalid: image.invalid,
            condition: image.frozen === true ? "Frozen" : image.frozen === false ? "Fresh" : '',
          }))
        );
        loadImages(response.cycle);
        setSelectedOocyte(Number(oocyteId ?? 1));
      })
      .catch((error) => {
        console.log("error", error);
      })
      .finally(() => {
        setLoading(false);
      });

    updateBreadcrumbs([
      {
        label: translations.breadcrumbs.oocyteQualityAssessment,
        onClick: () => navigate("/oocyte", { replace: true }),
      },
      {
        label: translations.breadcrumbs.history,
        onClick: () => navigate(`/oocyte/${cycleId}`, { replace: true }),
      },
      {
        label: translations.breadcrumbs.details,
      },
    ]);
  }, []);

  const saveNotes = () => {
    apiService
      .post(`/oocyte/${cycleId}/${selectedOocyte}`, {
        fields: {
          u_notes: notes,
        },
      })
      .then((response) => {
        console.log("response", response);
      })
      .catch((error) => {
        console.log("error", error);
        showError(translations.details.messages.notesUpdateFailed);
      });
    setNotesEditMode(false);
  };

  const cancelNotes = () => {
    setNotes(oocytes[selectedOocyte - 1]?.notes || "");
    setNotesEditMode(false);
  };

  useEffect(() => {
    if (score > 100) {
      setScore(Number(String(score).slice(0, String(score).length - 1)));
    }
    if (score < 0) {
      setScore(0);
    }
  }, [score]);

  const handleUpdateScore = useCallback(() => {
    if (!selectedOocyte) {
      return;
    }

    if (score === null) {
      showError(translations.form.errors.score.required);
      return;
    }

    setSubmitting(true);
    apiService
      .post(`/oocyte/${cycleId}/${selectedOocyte}`, {
        fields: {
          u_overall: score,
        },
      })
      .then((response) => {
        console.info("selectedOocyte", selectedOocyte, oocytes);
        showSuccess(translations.details.messages.scoreUpdated);
        setOocytes((prevState) => {
          const updatedOocytes = [...prevState];
          updatedOocytes[selectedOocyte - 1].score = score;
          return updatedOocytes;
        });

        setScoreEditMode(false);
      })
      .catch((error) => {
        console.log("error", error);
        showError(translations.details.messages.scoreUpdateFailed);
      })
      .finally(() => {
        setSubmitting(false);
      });
  }, [selectedOocyte, score]);

  useEffect(() => {
    setScore(oocytes[selectedOocyte - 1]?.score);
  }, [selectedOocyte]);

  const scoreTexts = [
    translations.details.scores.poor,
    translations.details.scores.fair,
    translations.details.scores.good,
    translations.details.scores.excellent,
  ];

  if (isLoading) {
    return <Progress />;
  }

  return (
    <Stack marginBottom={6} spacing={2}>
      <Stack alignItems="center" direction="row" spacing={2}>
        <Typography fontSize={fontSizes["2xl"]} variant="medium">
          {translations.details.card.header}
        </Typography>
      </Stack>
      <HorizontalSlider
        items={oocytes.map((o, index) => (
          <StyledOocyteIndex
            key={index}
            onClick={() => {
              if (o.invalid) {
                return;
              }
              setSelectedOocyte(index + 1);
            }}
            className={selectedOocyte === o.index ? "selected" : ""}
          >
            <Tooltip
              title={
                o.invalid ? translations.details.labels.oocyteImageInvalid : ""
              }
            >
              <Stack alignItems={"center"} direction={"row"} gap={1}>
                <Typography variant="caption" fontWeight={"bold"}>
                  {index + 1}
                </Typography>

                <StyledCircle
                  sx={{
                    backgroundColor: scoretoColor(oocytes[index]?.score),
                  }}
                />
              </Stack>
            </Tooltip>
          </StyledOocyteIndex>
        ))}
      />
      <Stack spacing={2}>
        <Stack direction={"row"} gap={2}>
          <Card sx={{ width: "100%" }}>
            <CardContent>
              <Box>
                <img
                  style={{ width: "100%", maxWidth: 400 }}
                  src={imageUrls[oocytes[selectedOocyte - 1]?.index] ?? ""}
                />
              </Box>
              <InfoRow
                label={translations.details.labels.retrievalDate}
                value={
                  oocytes[selectedOocyte - 1]?.retrievalDate
                    ? oocytes[selectedOocyte - 1].retrievalDate!.toLocaleDateString()
                    : ""
                }
              />
              <InfoRow
                label={translations.details.labels.timePostHarvesting}
                value={
                  oocytes[selectedOocyte - 1]?.timePostHarvesting
                    ? `${oocytes[selectedOocyte - 1]?.timePostHarvesting} ${
                        translations.details.labels.hours
                      }`
                    : ""
                }
              />
              <InfoRow
                label={translations.details.labels.condition}
                value={oocytes[selectedOocyte - 1]?.condition}
              />
            </CardContent>
          </Card>
          <Stack width={"100%"} spacing={2}>
            <Card>
              <CardContent>
                <Stack gap={2}>
                  <Stack
                    direction={"row"}
                    gap={2}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                  >
                    <Typography variant="h6">
                      {`${translations.details.card.oocyteNumber} ${selectedOocyte}`}
                    </Typography>

                    {scoreEditMode && (
                      <Stack direction={"row"} gap={2}>
                        <Button
                          color="whiteButton"
                          onClick={() => setScoreEditMode(false)}
                        >
                          {translations.details.buttons.cancel}
                        </Button>
                        <SubmitButton
                          onClick={handleUpdateScore}
                          color="blackButton"
                        >
                          {translations.details.buttons.save}
                        </SubmitButton>
                      </Stack>
                    )}
                  </Stack>
                  <Stack
                    gap={2}
                    direction={"row"}
                    alignItems={"center"}
                    borderRadius={3}
                    justifyContent={"flex-start"}
                    sx={{
                      backgroundColor: scoretoColor(
                        oocytes[selectedOocyte - 1]?.score
                      ),
                    }}
                  >
                    <Stack
                      direction={"row"}
                      gap={1}
                      alignItems={"center"}
                      sx={{ paddingX: 2, paddingY: 1 }}
                    >
                      {scoreEditMode ? (
                        <NoBorderTextField
                          variant="outlined"
                          type="number"
                          autoFocus
                          onFocus={() => setScoreEditMode(true)}
                          value={score}
                          onChange={(e) => setScore(parseInt(e.target.value))}
                        />
                      ) : (
                        <Stack
                          direction={"row"}
                          gap={1}
                          alignItems={"center"}
                          width={60}
                        >
                          <Typography
                            variant="report.headline"
                            fontWeight={"bold"}
                            fontSize={"1.4285714285714284rem"}
                          >
                            {oocytes[selectedOocyte - 1]?.score}
                          </Typography>
                          <IconButton onClick={() => setScoreEditMode(true)}>
                            <Pencil />
                          </IconButton>
                        </Stack>
                      )}
                    </Stack>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ bgcolor: "white" }}
                    />
                    <Box width={"100%"}>
                      <Typography variant="h6">
                        {scoretoText(
                          oocytes[selectedOocyte - 1]?.score,
                          scoreTexts
                        )}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction={"row"} gap={2}>
                    <Stack gap={1}>
                      <Typography variant="caption">
                        {translations.details.labels.fertilizationRate}
                      </Typography>
                      <Typography fontWeight={"bold"} variant="body2">
                        {oocytes[selectedOocyte - 1]?.invalid
                          ? ""
                          : oocytes[selectedOocyte - 1]?.fertilization}
                        %
                      </Typography>
                    </Stack>
                    <Stack gap={1}>
                      <Typography variant="caption">
                        {translations.details.labels.blastulationRate}
                      </Typography>
                      <Typography fontWeight={"bold"} variant="body2">
                        {oocytes[selectedOocyte - 1]?.invalid
                          ? ""
                          : oocytes[selectedOocyte - 1]?.blastulation}
                        %
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack gap={2}>
                  <Typography variant="h6">
                    {translations.details.card.progression}
                  </Typography>
                  {oocytes[selectedOocyte - 1]?.invalid ? null : (
                    <ProgressionChart
                      fertilization={oocytes[selectedOocyte - 1]?.fertilization ?? 0}
                      cleavage={oocytes[selectedOocyte - 1]?.cleavage ?? 0}
                      blastocyst={oocytes[selectedOocyte - 1]?.blastulation ?? 0}
                    />
                  )}
                </Stack>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Stack gap={2}>
                  <Stack
                    direction={"row"}
                    gap={2}
                    justifyContent={"space-between"}
                  >
                    <Typography variant="h6">
                      {translations.details.card.notes}
                    </Typography>
                    {notesEditMode ? (
                      <Stack direction={"row"} gap={2}>
                        <Button onClick={cancelNotes} color="whiteButton">
                          {translations.details.buttons.cancel}
                        </Button>
                        <Button onClick={saveNotes} color="blackButton">
                          {translations.details.buttons.save}
                        </Button>
                      </Stack>
                    ) : (
                      <IconButton onClick={() => setNotesEditMode(true)}>
                        <EditOutlined />
                      </IconButton>
                    )}
                  </Stack>
                  {notesEditMode ? (
                    <TextField
                      multiline
                      fullWidth
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  ) : notes.length > 0 ? (
                    <Typography variant="body2">{notes}</Typography>
                  ) : (
                    <Typography variant="subtitle2">
                      {translations.details.messages.noNotes}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default Details;
